import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useUnlayerTemplateContext } from '@/context/UnlayerTemplateContext';
import { useUnlayerComposeInteractionsContext } from '@/context/UnlayerComposeInteractionsContext';

/**
 * Handles auto-loading a preset template when the compose drawer opens with
 * dialog-provided template metadata.
 */
export const usePresetTemplateLoader = () => {
	const open = useSelector((state) => state.dialog?.open?.unlayerEmail) ?? false;
	const dialogData = useSelector((state) => state.dialog?.data?.unlayerEmail) ?? {};
	const presetTemplateName = useMemo(
		() => (dialogData?.templateName ?? '').trim(),
		[dialogData?.templateName],
	);

	const { handleTemplateSelect } = useUnlayerComposeInteractionsContext();
	const {
		hasLoadedTemplateSummaries,
		isFetchingTemplates,
		handleTemplateSelectorOpen,
		templateSummaries,
	} = useUnlayerTemplateContext();

	const [isPresetTemplateLookupRequested, setIsPresetTemplateLookupRequested] = useState(false);
	const [hasHandledPresetTemplateSelection, setHasHandledPresetTemplateSelection] = useState(false);
	const [hasObservedTemplateFetch, setHasObservedTemplateFetch] = useState(false);

	useEffect(() => {
		if (!open) {
			setIsPresetTemplateLookupRequested(false);
			setHasHandledPresetTemplateSelection(false);
			setHasObservedTemplateFetch(false);
		}
	}, [open, presetTemplateName]);

	useEffect(() => {
		if (!open || presetTemplateName.length === 0 || isPresetTemplateLookupRequested) return;
		handleTemplateSelectorOpen();
		setIsPresetTemplateLookupRequested(true);
	}, [
		open,
		presetTemplateName,
		isPresetTemplateLookupRequested,
		handleTemplateSelectorOpen,
	]);

	useEffect(() => {
		if (isPresetTemplateLookupRequested && isFetchingTemplates) {
			setHasObservedTemplateFetch(true);
		}
	}, [isPresetTemplateLookupRequested, isFetchingTemplates]);

	useEffect(() => {
		if (
			!open ||
			presetTemplateName.length === 0 ||
			!isPresetTemplateLookupRequested ||
			hasHandledPresetTemplateSelection ||
			isFetchingTemplates
		) {
			return;
		}

		const normalizedTemplateName = presetTemplateName.toLowerCase();
		const matchedTemplate = (templateSummaries ?? []).find(
			(template) => (template?.name ?? '').trim().toLowerCase() === normalizedTemplateName,
		);

		if (matchedTemplate?.id) {
			setHasHandledPresetTemplateSelection(true);
			handleTemplateSelect(matchedTemplate.id);
			return;
		}

		if (
			!hasObservedTemplateFetch &&
			!hasLoadedTemplateSummaries &&
			(templateSummaries ?? []).length === 0
		) {
			return;
		}

		setHasHandledPresetTemplateSelection(true);
	}, [
		hasHandledPresetTemplateSelection,
		hasLoadedTemplateSummaries,
		hasObservedTemplateFetch,
		handleTemplateSelect,
		isFetchingTemplates,
		isPresetTemplateLookupRequested,
		open,
		presetTemplateName,
		templateSummaries,
	]);
};
